import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { observer } from 'mobx-react';
import cls from 'classnames';

import SystemAlerts from '@/alert/components/SystemAlerts/SystemAlerts';
import {
  GO_TO_COLLECTIONS_FROM_FOOTER,
  GO_TO_PRESENTATION_FROM_FOOTER,
  GO_TO_PRICING_FROM_FOOTER,
  GO_TO_PRIVACY_FROM_FOOTER,
  GO_TO_WORKSHOP,
} from '@/analytics/services/Analytics';
import { useAnalyticsStore } from '@/analytics/store/AnalyticsStore';
import AboutPopover from '@/common/components/Header/AboutPopover/AboutPopover';
import CollectionOrDriveNavItem from '@/common/components/Header/CollectionOrDriveNavItem/CollectionOrDriveNavItem';
import ComparisonNavItem from '@/common/components/Header/ComparisonNavItem/ComparisonNavItem';
import DiamondsNavItem from '@/common/components/Header/DiamondsNavItem/DiamondsNavItem';
import MobileDrawer from '@/common/components/Header/MobileDrawer/MobileDrawer';
import UserNavDropdown from '@/common/components/Header/UserNavDropdown/UserNavDropdown';
import { HeaderSection } from '@/common/constants/HeaderSection';
import { HOME_PAGE } from '@/common/constants/pages';
import { Rollout } from '@/common/constants/rollouts';
import NetBoxIndicator from '@/netbox/components/NetBoxIndicator/NetBoxIndicator';
import { AuthModalConfirmation } from '@/user/components/AuthModal/AuthModal';
import AuthService from '@/user/services/AuthService';
import Token from '@/user/services/token';
import { useUserStore } from '@/user/stores/UserStore';

import { SCREEN_MD } from '../../constants/breakpoints';
import { useAppStore } from '../../stores/AppStore';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Link, { NAV, NONE } from '../Link/Link';
import Logo from '../Logo/Logo';

import styles from './Header.module.css';

interface HeaderProps {
  fixed?: boolean;
}

function Header(props: HeaderProps) {
  const { fixed = true } = props;
  const userStore = useUserStore();
  const appStore = useAppStore();
  const analyticsStore = useAnalyticsStore();
  const location = useLocation();
  const [isAboutPopoverOpened, setIsAboutPopoverOpened] = useState(false);
  const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const openMobileMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const button = (e.target as HTMLElement).closest('button');

      if (button) {
        button.blur();
      }

      if (isMobileMenuOpened) {
        return;
      }

      setIsMobileMenuOpened(true);
    },
    [isMobileMenuOpened],
  );

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpened(false);
  }, []);

  const closeAboutPopover = useCallback(() => {
    setIsAboutPopoverOpened(false);
  }, []);

  const handleCollectionsClick = useCallback(() => {
    analyticsStore.sendEventWithContext(GO_TO_COLLECTIONS_FROM_FOOTER);
    closeMobileMenu();
    closeAboutPopover();
  }, [analyticsStore, closeMobileMenu, closeAboutPopover]);

  const handlePresentationClick = useCallback(() => {
    analyticsStore.sendEventWithContext(GO_TO_PRESENTATION_FROM_FOOTER);
    closeMobileMenu();
  }, [analyticsStore, closeMobileMenu]);

  const handleWorkshopClick = useCallback(() => {
    analyticsStore.sendEventWithContext(GO_TO_WORKSHOP);
    closeMobileMenu();
  }, [analyticsStore, closeMobileMenu]);

  const handlePricingClick = useCallback(() => {
    analyticsStore.sendEventWithContext(GO_TO_PRICING_FROM_FOOTER);
    closeMobileMenu();
  }, [analyticsStore, closeMobileMenu]);

  const handlePrivacyPolicyClick = useCallback(() => {
    analyticsStore.sendEventWithContext(GO_TO_PRIVACY_FROM_FOOTER);
    closeMobileMenu();
  }, [analyticsStore, closeMobileMenu]);

  const handleLogout = useCallback(() => {
    try {
      navigator.sendBeacon(AuthService.generateLogoutUrl());
    } catch (e) {
      // do nothing
    }

    AuthService.logout();
  }, []);

  const redirectToLoginPage = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    AuthModalConfirmation();
  }, []);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        document.documentElement.style.setProperty('--header-height', `${entry.contentRect.height}px`);
      }
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <header ref={ref} className={cls(styles.header, { [styles.headerFixed]: fixed }, styles.headerDisplayed)}>
      <SystemAlerts />

      <div className={styles.headerContent}>
        <div className={styles.headerHm}>
          <Button
            color="transparent"
            onClick={openMobileMenu}
            withIcon
            aria-label="Header Menu Sections Control for Mobile Devices"
          >
            <Icon name="hamburger" />
          </Button>
        </div>

        <div className={styles.headerLeft}>
          <div className={styles.headerLogo}>
            <Link aria-label="Home" to={Token.isAuthorized ? '/catalog/diamond-colorless' : '/'} appearance={NONE}>
              <Logo />
            </Link>
          </div>

          <div className={styles.headerCacheProxy}>
            <NetBoxIndicator />
          </div>
        </div>

        {appStore.screenWidth && appStore.screenWidth < SCREEN_MD && (
          <MobileDrawer
            isOpened={isMobileMenuOpened}
            closeMobileMenu={closeMobileMenu}
            onCollectionClick={handleCollectionsClick}
            onPresentationClick={handlePresentationClick}
            onWorkshopClick={handleWorkshopClick}
            onPrivacyPolicyClick={handlePrivacyPolicyClick}
            onPricingClick={handlePricingClick}
            onLogout={handleLogout}
            redirectToLoginPage={redirectToLoginPage}
          />
        )}

        <div className={styles.headerChapters} role="menu" aria-label="Header Menu Sections">
          <DiamondsNavItem
            isActive={appStore.headerSection === HeaderSection.NaturalDiamonds}
            title="Natural Diamonds"
            colorlessLink="/catalog/diamond-colorless"
            fancyLink="/catalog/diamond-fancy"
            roughLink="/catalog/rough"
          />

          <DiamondsNavItem
            isActive={appStore.headerSection === HeaderSection.LabGrownDiamonds}
            title="Lab-Grown Diamonds"
            colorlessLink="/catalog/lgd-colorless"
            fancyLink="/catalog/lgd-fancy"
            roughLink="/catalog/lgd-rough"
            navClassName={styles.headerLinkLGD}
          />

          <Link
            aria-label="Jewelry"
            to="/catalog/jewelry"
            className={cls(styles.headerNavLink, styles.headerNavLinkDesktop, {
              [styles.headerLinkActive]: appStore.headerSection === HeaderSection.Jewelry,
            })}
            role="menuitem"
            appearance={NONE}
          >
            Jewelry
          </Link>

          <Link
            aria-label="Gems"
            to="/catalog/gem"
            className={cls(styles.headerNavLink, styles.headerNavLinkDesktop, {
              [styles.headerLinkActive]: appStore.headerSection === HeaderSection.Gems,
            })}
            role="menuitem"
            appearance={NONE}
          >
            Gems
          </Link>

          <Link
            aria-label="Demo"
            className={cls(styles.headerNavLink, styles.headerNavLinkDesktop, styles.hideOnSmallScreens, {
              [styles.headerLinkActive]: appStore.headerSection === HeaderSection.Demo,
            })}
            onClick={handleCollectionsClick}
            to="/collections"
            role="menuitem"
            appearance={NONE}
          >
            Demo
          </Link>

          <AboutPopover
            isOpened={isAboutPopoverOpened}
            onCollectionsClick={handleCollectionsClick}
            onPresentationClick={handlePresentationClick}
            onWorkshopClick={handleWorkshopClick}
            onPricingClick={handlePricingClick}
            onPrivacyPolicyClick={handlePrivacyPolicyClick}
            close={closeAboutPopover}
            onChangeState={setIsAboutPopoverOpened}
          >
            <div
              aria-label="About"
              className={cls(styles.headerNavLink, styles.headerNavLinkDesktop, styles.headerNavLinkDropdown)}
            >
              <span>About </span>

              <Icon
                name="arrow-lt-down"
                className={cls(styles.headerNavLinkTriangle, {
                  [styles.headerNavLinkTriangleOpened]: isAboutPopoverOpened,
                })}
              />
            </div>
          </AboutPopover>
        </div>

        <div className={styles.headerLinks}>
          <CollectionOrDriveNavItem />

          {location.pathname !== HOME_PAGE && !userStore.user?.hasRollout(Rollout.Drive) && <ComparisonNavItem />}

          {userStore.isAuthorized ? (
            <UserNavDropdown user={userStore.user!} logout={handleLogout} />
          ) : (
            <Link
              aria-label="Log in"
              className={cls(styles.headerLink, styles.headerLinkDesktop)}
              href={AuthService.generateAuthUrl()}
              onClick={redirectToLoginPage}
              appearance={NAV}
            >
              Log in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export default observer(Header);
